import React from "react";
import PropTypes from "prop-types";
import { SingleWithLabelAndHelpText } from "../../../../components/atoms";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";
import MultipleWithLabelAndHelpText from "../../../../components/atoms/MultipleWithLabelAndHelpText/MultipleWithLabelAndHelpText";
import AdditionalLinksOther from "./AdditionalLinkOthers/AdditionalLinksOther";
import AdditionalLinksResources from "./AdditionalLinkResources/AdditionalLinkResources";
import DeclarationList from "../../../../components/declaration/DeclareInterestList";
import Link from "./Link";
import PartnerNotice from "../../../../components/partner/PartnerNotice";
import HelperText from "../../../../components/atoms/HelperText";

const HGNCUrl = "https://monarchinitiative.org/HGNC:";
const EnsemblUrl = "https://www.ensembl.org/Homo_sapiens/Gene/Summary?g=";
const EntrezUrl = "https://www.ncbi.nlm.nih.gov/gene/?term=";
const UniProtUrl = "https://www.uniprot.org/uniprot/";

const GeneBasicInfo = (props) => {
  const { selectedGene, genes, customMessage } = props;
  if (customMessage) {
    // Custom message is used in clinical in order to display
    // the deleted data reason message. Eg. The detailed data
    // for non-coding genes has been removed as the variant affects more than 30 genes. Since we
    // need at least one card available for the cards ui to make sense the deleted data reason messages,
    // will be displayed here.
    return customMessage;
  }
  if (!selectedGene) return null;
  const { transcripts } = selectedGene || {};
  let geneCytobands;
  if (transcripts) {
    const cytobands = [];
    for (let i = 0; i < transcripts.length; i++) {
      const t = transcripts[i];
      if (t.cytoband && cytobands.indexOf(t.cytoband) === -1) {
        cytobands.push(t.cytoband);
      }
    }
    if (cytobands.length) {
      geneCytobands = (
        <div className="varsome-horiz-list-item">
          <div className="varsome-horiz-list-label">
            {"Cytobands "}
            <HelperText fieldDescriptionKey="Cytobands" />
          </div>
          <div
            className="varsome-horiz-list-value"
            data-testid="cytobands-value"
          >
            {cytobands.map((c, i) => (
              <div key={i}>{c}</div>
            ))}
          </div>
        </div>
      );
    }
  }

  const collab = (
    <div className="varsome-inner-container">
      <DeclarationList genes={genes} />
    </div>
  );

  const template = (
    <div className="varsome-inner-container">
      {!__IS_COMPONENTS_PORTAL__ && <PartnerNotice />}
      <div className="varsome-horiz-flex">
        <SingleWithLabelAndHelpText
          item={selectedGene}
          prop="description"
          label="Description"
          crop={false}
        />
        <MultipleWithLabelAndHelpText
          item={selectedGene}
          prop="synonyms"
          label="Synonyms"
        />
        {selectedGene.identifiers && (
          <div className="varsome-horiz-list-item">
            <div className="varsome-horiz-list-label">Gene identifiers</div>
            <div
              className="varsome-horiz-list-value"
              data-testid="identifiers-value"
            >
              {Array.isArray(selectedGene.identifiers.hgnc_previous_symbol) &&
                selectedGene.identifiers.hgnc_previous_symbol.map((id, i) => {
                  const key = `hgncPrevious${i}`;
                  return (
                    <div key={key}>
                      {"HGNC previous symbol: "}
                      {id}
                    </div>
                  );
                })}
              <Link
                name="hgnc_previous_symbol"
                label="HGNC"
                url={HGNCUrl}
                selectedGene={selectedGene}
              />
              <Link
                name="ensembl_id"
                label="Ensembl"
                url={EnsemblUrl}
                selectedGene={selectedGene}
              />
              <Link
                name="entrez_id"
                label="Entrez"
                url={EntrezUrl}
                selectedGene={selectedGene}
              />
              <Link
                name="uniprot_id"
                label="UniProt"
                url={UniProtUrl}
                selectedGene={selectedGene}
              />
            </div>
          </div>
        )}
        {geneCytobands}
      </div>
      <div className="varsome-horiz-flex">
        <AdditionalLinksResources selectedGene={selectedGene} />
        <AdditionalLinksOther selectedGene={selectedGene} />
      </div>
      {!__IS_COMPONENTS_PORTAL__ && collab}
    </div>
  );

  return (
    <div
      className="varsome-result-fixed"
      data-testid="gene-basic-info-component"
    >
      <div className="varsome-container varsome-container__basic">
        <ComponentPanel title="Gene basic info" content={template} />
      </div>
    </div>
  );
};

GeneBasicInfo.propTypes = {
  selectedGene: PropTypes.shape({
    transcripts: PropTypes.arrayOf(
      PropTypes.shape({
        canonical: PropTypes.bool,
        chromo: PropTypes.string,
        coding_length: PropTypes.number,
        cytoband: PropTypes.string,
        ensembl_support_level: PropTypes.string,
        length: PropTypes.number,
        mrna_length: PropTypes.number,
        name: PropTypes.string,
        position: PropTypes.number,
        strand: PropTypes.string,
      })
    ),
    identifiers: PropTypes.shape({
      hgnc_previous_symbol: PropTypes.arrayOf(PropTypes.string),
      lrg_id: PropTypes.arrayOf(PropTypes.string),
      ensembl_id: PropTypes.arrayOf(PropTypes.string),
      entrez_id: PropTypes.arrayOf(PropTypes.string),
      hgnc_id: PropTypes.arrayOf(PropTypes.string),
      uniprot_id: PropTypes.arrayOf(PropTypes.string),
    }),
  }),
  genes: PropTypes.arrayOf(PropTypes.shape({})),
  customMessage: PropTypes.element,
};

GeneBasicInfo.defaultProps = {
  selectedGene: null,
  genes: null,
  customMessage: null,
};

export default GeneBasicInfo;
